import { memo } from 'react';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, List, ListItem,
  ListItemText, Tooltip, Typography,
} from '@mui/material';
import { KIND_CEILING } from '../catalog';
import { useToolAccess } from '../hooks/useToolConfigs';
import { useKindChange } from '../hooks/useKindChange';
import { KINDS, kindName, writes } from '../utils/toolKinds';
import type { InvocationShapeType } from '../../../types/backend';
import StoredReadFailed from './StoredReadFailed';

interface ToolKindsProps {
  actionId: string;
  /** The stored chat and expert mark; undefined means none is stored, so every such kind the ceiling allows is
   * offered. Automation is not in it: it is the tool's automation tick. */
  stored?: InvocationShapeType[];
  editable: boolean;
}

/**
 * Which agent kinds the tool is offered to. Chat and experts come from the kind mark; automation from the tool's
 * automation tick, read from its access. A kind the code ceiling does not allow is locked with its reason. A change
 * is previewed first: the dialog names the active agents that lose the tool, and applying sends exactly those
 * agents, so the server refuses it if they changed meanwhile.
 */
const ToolKinds = memo<ToolKindsProps>(({ actionId, stored, editable }) => {
  const ceiling = KIND_CEILING[actionId];
  if (!ceiling) throw new Error(`No kind ceiling for ${actionId}: regenerate the tool catalog`);
  const automationAllowed = ceiling.batch === null;
  const { data: access, isError: accessReadFailed } = useToolAccess(actionId, automationAllowed);
  // A failed read is not an unticked box (FE-14): the automation box shows the error instead.
  const tickUnreadable = automationAllowed && accessReadFailed;
  const tickLoading = automationAllowed && !access && !tickUnreadable;
  const ticked = automationAllowed && access?.access.access_batch === 'on';
  const allowed = KINDS.filter(({ kind }) => ceiling[kind] === null).map(({ kind }) => kind);
  const current = [
    ...(stored ?? allowed).filter((k) => k !== 'batch'),
    ...(ticked ? ['batch' as const] : []),
  ];

  const change = useKindChange(actionId, ticked);
  const { pending, blocking, consulted } = change;

  // A change sends every kind, so every box waits for the tick: with it unread or still loading, a chat or expert
  // change would send automation as unticked.
  const tickUnknown = tickUnreadable || tickLoading;
  const toggle = (kind: InvocationShapeType) => {
    if (tickUnknown) return;
    void change.preview(KINDS.map((k) => k.kind).filter((k) => (k === kind ? !current.includes(k) : current.includes(k))));
  };

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Offered to</Typography>
      {KINDS.map(({ kind, label }) => {
        const reason = ceiling[kind];
        if (kind === 'batch' && reason === null && tickUnreadable) {
          return (
            <Box key={kind} data-testid={`tool-kind-${kind}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
              <Typography variant="body2">{label}</Typography>
              <StoredReadFailed />
            </Box>
          );
        }
        const control = (
          <FormControlLabel
            data-testid={`tool-kind-${kind}`}
            label={<Typography variant="body2">{label}</Typography>}
            control={(
              <Checkbox
                size="small"
                checked={reason === null && current.includes(kind)}
                disabled={!editable || reason !== null || change.busy || tickUnknown}
                onChange={() => toggle(kind)}
              />
            )}
          />
        );
        return (
          <Box key={kind}>
            {reason ? <Tooltip title={reason}><span>{control}</span></Tooltip> : control}
            {reason && (
              <Typography data-testid={`tool-kind-${kind}-reason`} variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                {reason}
              </Typography>
            )}
            {kind === 'batch' && reason === null && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                Experts consulted by automation need this mark too
              </Typography>
            )}
          </Box>
        );
      })}
      {editable && stored !== undefined && (
        <Button size="small" sx={{ textTransform: 'none', px: 0 }} onClick={() => change.preview(null)}>
          Offer to every allowed chat and expert kind
        </Button>
      )}
      {change.error && <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>{change.error}</Typography>}

      <Dialog open={pending !== null} onClose={change.cancel}>
        <DialogTitle>Change who is offered this tool</DialogTitle>
        <DialogContent>
          {change.ticking && writes(actionId) && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
              Unattended runs will be able to change customer environments with nobody approving.
            </Typography>
          )}
          {change.unticking && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Every automation run is refused it from the next call, including workflows and rules.
            </Typography>
          )}
          {pending && pending.removals.length === 0 ? (
            <Typography variant="body2">No active agent loses this tool.</Typography>
          ) : (
            <>
              <Typography variant="body2">These agents lose the tool when you apply:</Typography>
              <List dense>
                {pending?.removals.map((r) => (
                  <ListItem key={r.agent_id}>
                    <ListItemText
                      primary={r.display_name}
                      secondary={[
                        `${r.agent_id} · ${kindName(r.shape)}`,
                        r.stops_consulting?.length ? `also stops consulting ${r.stops_consulting.join(', ')}` : null,
                      ].filter(Boolean).join(' · ')}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
          {consulted.length > 0 && (
            <>
              <Typography variant="body2">These experts keep the tool, and are refused it when consulted by automation:</Typography>
              <List dense>
                {consulted.map((r) => (
                  <ListItem key={r.agent_id}>
                    <ListItemText primary={r.display_name} secondary={`${r.agent_id} · ${kindName(r.shape)}`} />
                  </ListItem>
                ))}
              </List>
            </>
          )}
          {blocking.map((r) => (
            <Typography key={r.agent_id} variant="body2" color="error.main">
              {r.display_name} would be left with no tools, and an agent cannot run with none. Give it another tool or
              retire it first.
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={change.cancel}>Cancel</Button>
          <Button variant="contained" onClick={change.apply} disabled={change.applying || blocking.length > 0}>Apply</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

ToolKinds.displayName = 'ToolKinds';

export default ToolKinds;
